import { useEffect, useLayoutEffect } from 'react'

/**
 * SC-10999: "empresa ativa" da visão multi-empresas.
 *
 * Na visão do grupo as listas vêm agregadas, mas toda ação (detalhe, editar,
 * cancelar, criar, autocompletes do formulário) precisa ir com o token da
 * empresa do registro. Em vez de passar a empresa por cada chamada dos modais
 * e formulários, quem abre um modal/drawer/formulário ligado a uma empresa
 * registra essa empresa aqui enquanto estiver aberto: as chamadas SEM escopo
 * explícito passam a usar o token dela (ver `getAPIClient`).
 *
 * É uma pilha com contagem por registro: um modal aberto por cima de outro
 * vence enquanto estiver aberto e, ao fechar, a empresa do de baixo volta.
 * Sem nada registrado, as chamadas seguem com o `nextauth.token`, como sempre.
 */

type Entry = { id: number; merchantUuid: string }

let stack: Entry[] = []
let sequence = 0

// Empresa de cada registro das listas agregadas, para os modais de detalhe
// abertos só com o uuid do registro descobrirem a empresa sem mudar a API de
// cada modal.
const recordMerchants = new Map<string, string>()

export const getActiveMerchantUuid = (): string | undefined =>
  stack.length ? stack[stack.length - 1].merchantUuid : undefined

/** Registra a empresa ativa e devolve a função que desfaz o registro. */
export const pushActiveMerchant = (merchantUuid: string) => {
  const id = ++sequence
  stack = [...stack, { id, merchantUuid }]

  let released = false
  return () => {
    if (released) return
    released = true
    stack = stack.filter((entry) => entry.id !== id)
  }
}

export const clearActiveMerchants = () => {
  stack = []
  recordMerchants.clear()
}

export const registerRecordMerchant = (
  recordUuid?: string,
  merchantUuid?: string
) => {
  if (recordUuid && merchantUuid) recordMerchants.set(recordUuid, merchantUuid)
}

export const getRecordMerchant = (recordUuid?: string) =>
  recordUuid ? recordMerchants.get(recordUuid) : undefined

// Layout effect: roda antes dos `useEffect` dos filhos, que é onde os modais
// fazem a primeira busca. Com `useEffect` a busca do filho sairia antes do
// registro, com o token errado.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Mantém `merchantUuid` como empresa ativa enquanto o componente estiver
 * montado. Sem empresa (fora da visão do grupo, ou registro sem empresa)
 * não faz nada.
 */
export const useActiveMerchant = (merchantUuid?: string | null) => {
  useIsomorphicLayoutEffect(() => {
    if (!merchantUuid) return

    return pushActiveMerchant(merchantUuid)
  }, [merchantUuid])
}

/** Igual ao `useActiveMerchant`, a partir do uuid de um registro da lista. */
export const useActiveMerchantForRecord = (recordUuid?: string | null) => {
  useActiveMerchant(getRecordMerchant(recordUuid || undefined))
}
